import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  ReactNode,
} from 'react';

import type { GeofenceAreaConfig } from '../types';
import { geofenceAreaConfigAPI } from '../utils/apiClient';
import logger from '../utils/logger';

import { useAuth } from './AuthContext';

/**
 * GeofenceAreaContext — shared source of truth for geofence area configs on the timespace map.
 *
 * Previously MapView and the GeofenceAreas overlay each fetched `geofenceAreaConfigAPI.list()`
 * independently, starting from an empty array. That left the beacon/checkin suppression set
 * empty on first paint, so geofenced infrastructure flashed as scattered point markers until
 * the fetch resolved. MapView and the GeofenceAreas layer now both read from this provider.
 *
 * (Sideways still fetches its own copy for symbol-key mapping, which this provider does not
 * expose; it is not backed by this context.)
 *
 * This provider fetches once on authentication and exposes a `loaded` flag so the
 * map can hold beacon/checkin markers until the suppression set is known.
 */
interface GeofenceAreaContextValue {
  /** All geofence area configs for the account. */
  areaConfigs: GeofenceAreaConfig[];
  /** device_ids of beacons/checkins that belong to an area with a drawn shape (point markers suppressed). */
  devicesWithAreaShape: Set<string>;
  /** True once the configs have been fetched at least once (success or failure). */
  loaded: boolean;
  /** Re-fetch the configs (e.g. after an area is edited). */
  refresh: () => void;
}

const GeofenceAreaContext = createContext<GeofenceAreaContextValue | null>(null);

export const useGeofenceAreas = (): GeofenceAreaContextValue => {
  const ctx = useContext(GeofenceAreaContext);
  if (!ctx) {
    throw new Error('useGeofenceAreas must be used within a GeofenceAreaProvider');
  }
  return ctx;
};

interface GeofenceAreaProviderProps {
  children: ReactNode;
}

export const GeofenceAreaProvider: React.FC<GeofenceAreaProviderProps> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [areaConfigs, setAreaConfigs] = useState<GeofenceAreaConfig[]>([]);
  const [loaded, setLoaded] = useState<boolean>(false);
  const [refreshKey, setRefreshKey] = useState<number>(0);

  const refresh = useCallback(() => setRefreshKey((k) => k + 1), []);

  useEffect(() => {
    if (!isAuthenticated) {
      setAreaConfigs([]);
      setLoaded(false);
      return;
    }
    let cancelled = false;
    const timers: ReturnType<typeof setTimeout>[] = [];

    // Retry a few times before failing open. Failing open with empty configs makes the
    // fresh-login flicker permanent for the session, so it's worth a couple of retries
    // to recover from a transient blip before giving up.
    const MAX_ATTEMPTS = 3;
    const RETRY_DELAY_MS = 3000;

    const attempt = (n: number): void => {
      geofenceAreaConfigAPI
        .list()
        .then((result) => {
          if (cancelled) return;
          setAreaConfigs(result.configs ?? []);
          setLoaded(true);
        })
        .catch((err) => {
          if (cancelled) return;
          logger.error(`Failed to load geofence area configs (attempt ${n}/${MAX_ATTEMPTS}):`, err);
          if (n < MAX_ATTEMPTS) {
            timers.push(setTimeout(() => attempt(n + 1), RETRY_DELAY_MS));
          } else {
            // Fail open: mark loaded so markers aren't hidden forever after exhausting retries.
            setLoaded(true);
          }
        });
    };
    attempt(1);

    return () => {
      cancelled = true;
      timers.forEach(clearTimeout);
    };
  }, [isAuthenticated, refreshKey]);

  // device_ids for beacons/checkins that belong to an area with a shape — their point
  // markers are suppressed so only the area overlay is shown.
  const devicesWithAreaShape = useMemo(
    () =>
      new Set(
        areaConfigs
          .filter((cfg) => cfg.area_shape)
          .flatMap((cfg) => [
            ...cfg.beacons.map((b) => b.device_id),
            ...cfg.checkins.map((c) => c.device_id),
          ])
      ),
    [areaConfigs]
  );

  const value = useMemo(
    () => ({ areaConfigs, devicesWithAreaShape, loaded, refresh }),
    [areaConfigs, devicesWithAreaShape, loaded, refresh]
  );

  return <GeofenceAreaContext.Provider value={value}>{children}</GeofenceAreaContext.Provider>;
};

export default GeofenceAreaProvider;
